import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams?.get('state');
        const errorParam = urlParams?.get('error');

        // Handle error from OAuth provider
        if (errorParam) {
          console.error('OAuth error:', errorParam);
          if (state === 'google_ads') {
            navigate('/settings?tab=integrations&oauth=google&status=error&msg=' + encodeURIComponent('Authorization was denied'), { replace: true });
            return;
          }
          setError('Authorization was denied or failed. Redirecting to login...');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Wait for Supabase to process the OAuth callback and establish session
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

        if (sessionError) {
          console.error('Auth callback error:', sessionError);
          if (state === 'google_ads') {
            navigate('/settings?tab=integrations&oauth=google&status=error&msg=' + encodeURIComponent(sessionError?.message || 'Session error'), { replace: true });
            return;
          }
          setError(sessionError?.message);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Handle Google Ads OAuth callback (state=google_ads)
        if (state === 'google_ads') {
          setStatus('Connecting Google Ads...');
          try {
            if (!session) {
              throw new Error('No session found after OAuth. Please try again.');
            }

            const providerToken = session?.provider_token;
            const providerRefreshToken = session?.provider_refresh_token;

            if (!providerRefreshToken) {
              throw new Error('No refresh token received from Google. Please ensure you granted offline access.');
            }

            // Get current user's organization
            const { data: profile } = await supabase
              ?.from('profiles')
              ?.select('org_id')
              ?.eq('id', session?.user?.id)
              ?.single();

            if (!profile?.org_id) {
              throw new Error('Organization not found. Please contact support.');
            }

            // Save refresh token to organizations table
            const { error: updateError } = await supabase
              ?.from('organizations')
              ?.update({ google_ads_refresh_token: providerRefreshToken })
              ?.eq('id', profile?.org_id);

            if (updateError) throw updateError;

            navigate('/settings?tab=integrations&oauth=google&status=success', { replace: true });
          } catch (adsErr) {
            console.error('Google Ads OAuth error:', adsErr);
            navigate('/settings?tab=integrations&oauth=google&status=error&msg=' + encodeURIComponent(adsErr?.message || 'Connection failed'), { replace: true });
          }
          return;
        }

        // Handle regular Supabase Auth OAuth callback (Google login, etc.)
        if (session?.user) {
          navigate('/dashboard', { replace: true });
        } else {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams?.get('access_token');
          const refreshToken = hashParams?.get('refresh_token');

          if (accessToken) {
            const { error: setSessionError } = await supabase?.auth?.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (!setSessionError) {
              navigate('/dashboard', { replace: true });
              return;
            }
          }

          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback exception:', err);
        setError('Authentication failed. Redirecting to login...');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-foreground font-medium">Authentication Error</p>
          <p className="text-muted-foreground text-sm">{error}</p>
          <p className="text-muted-foreground text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 p-8">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-foreground font-medium">{status}</p>
        <p className="text-muted-foreground text-sm">Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
